import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Upload, Trash2, Plus, GripVertical, Settings, Users, 
  Bed, Image as ImageIcon, Search, Loader2, Save, FileDown,
  Map, CalendarDays, Building, CircleDollarSign, CheckSquare, FileText, ChevronLeft, Building2, MapPin, Edit2, Check, Wand2, Share2, Clock, Copy, Eye, Wallet, RotateCw, ExternalLink, RefreshCw, X
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ItineraryPreview from "@/components/ItineraryPreview";

export default function ItineraryEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeDay, setActiveDay] = useState(0);
  const [activeNight, setActiveNight] = useState(0);
  const [imgDialog, setImgDialog] = useState({ open: false, dayIdx: null, imgIdx: null });
  const [aiPrompt, setAiPrompt] = useState("");
  const [imgQuery, setImgQuery] = useState("");
  const [imgResults, setImgResults] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [shareDays, setShareDays] = useState(1);
  const [shareResult, setShareResult] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [hotelSuggestions, setHotelSuggestions] = useState({});
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    packageIdx: 0,
    sellingPrice: 0,
    hotels: [],
    transports: [],
    activities: []
  });
  const [viewInvoiceDialog, setViewInvoiceDialog] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState("trip");
  const [showPreviewPane, setShowPreviewPane] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);
  
  // Ledger States
  const [ledgerDialog, setLedgerDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'IN',
    party: '',
    bankAccount: '',
    amount: '',
    reference: '',
    notes: ''
  });
  const [editingTxId, setEditingTxId] = useState(null);
  
  // AI Receipt Scanner States
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ file: null, context: '', loading: false });
  const [clarificationMsg, setClarificationMsg] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-populate invoice form when packageIdx changes or modal opens
  useEffect(() => {
    if (!invoiceDialog || !record) return;
    
    const pkg = record.packages?.[invoiceForm.packageIdx];
    if (!pkg) return;
    
    // Auto-fill selling price
    const sp = parseFloat(pkg.price_total) || 0;
    
    // Gather unique hotels from itinerary for this package
    const uniqueHotels = new Set();
    const hotelsArr = [];
    if (record.itinerary_data?.days) {
      record.itinerary_data.days.forEach(d => {
        const hName = (d.package_hotels?.[invoiceForm.packageIdx]?.hotel_name || d.hotel_name || "").trim();
        if (hName && !["na", "n/a", "nil", "none", "-", "--"].includes(hName.toLowerCase()) && !uniqueHotels.has(hName)) {
          uniqueHotels.add(hName);
          hotelsArr.push({ name: hName, cost: 0 });
        }
      });
    }
    
    // If no specific hotels found, add a generic one
    if (hotelsArr.length === 0) {
      hotelsArr.push({ name: "Hotel Accommodation", cost: 0 });
    }
    
    // Set initial form state, preserving existing values if they were already typed (basic check: if we already have hotels, don't overwrite)
    setInvoiceForm(prev => {
      if (prev.hotels.length > 0 && prev.sellingPrice > 0) return prev; // don't override if user already started typing
      return {
        ...prev,
        sellingPrice: sp,
        hotels: hotelsArr,
        transports: [{ name: pkg.transport || "Transportation", cost: 0 }],
        activities: [{ name: "Sightseeing & Activities", cost: 0 }]
      };
    });
  }, [invoiceDialog, invoiceForm.packageIdx, record]);

  const load = useCallback(() => {
    setLoading(true);
    api.getItinerary(id).then(setRecord).catch((e) => { toast.error("Failed to load"); console.error(e); }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Fetch hotel suggestions for cities
  useEffect(() => {
    if (!record?.itinerary_data?.days) return;
    record.itinerary_data.days.forEach((d, dayIdx) => {
      let stayCity = (d.accommodation || "").trim();
      const lowerAccom = stayCity.toLowerCase();
      const isNoStay = !stayCity || 
        ["na", "n/a", "nil", "none", "-", "--"].includes(lowerAccom) ||
        /overnight.*(travel|journey|bus|train|flight|cab|volvo)/i.test(lowerAccom) ||
        /travel.*to/i.test(lowerAccom) ||
        lowerAccom.includes("departure");
        
      if (!isNoStay) {
        if (stayCity.toLowerCase().includes(" in ")) {
          stayCity = stayCity.split(/ in /i).pop().trim();
        }
        api.searchHotels(stayCity).then(res => {
          if (res.items && res.items.length > 0) {
            setHotelSuggestions(prev => ({ ...prev, [dayIdx]: res.items }));
          }
        }).catch(() => {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  if (loading) return <div className="py-20 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!record) return <div className="py-20 text-center text-slate-600">Not found</div>;

  const data = record.itinerary_data || {};
  const days = data.days || [];

  const updateField = (path, value) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      // path supports "itinerary_data.days.0.description"
      const keys = path.split(".");
      let cur = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (Array.isArray(cur)) cur = cur[Number(k)];
        else cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const handlePaymentSubmit = async () => {
    try {
      const updatedRecord = { ...record };
      if (!updatedRecord.itinerary_data.ledger) {
        updatedRecord.itinerary_data.ledger = { transactions: [] };
      }
      if (!updatedRecord.itinerary_data.ledger.transactions) {
        updatedRecord.itinerary_data.ledger.transactions = [];
      }
      const newTx = {
        id: editingTxId ? editingTxId : "tx_" + Date.now(),
        ...paymentForm,
        amount: parseFloat(paymentForm.amount) || 0
      };

      if (editingTxId) {
        const idx = updatedRecord.itinerary_data.ledger.transactions.findIndex(t => t.id === editingTxId);
        if (idx > -1) updatedRecord.itinerary_data.ledger.transactions[idx] = newTx;
      } else {
        updatedRecord.itinerary_data.ledger.transactions.push(newTx);
      }
      
      
      setRecord(updatedRecord);
      setLedgerDialog(false);
      setPaymentForm({
        date: new Date().toISOString().split('T')[0],
        type: 'IN',
        party: '',
        bankAccount: '',
        amount: '',
        reference: '',
        notes: ''
      });
      setEditingTxId(null);
      toast.success(editingTxId ? "Payment updated!" : "Payment recorded!");
      
      setSaving(true);
      await api.updateItinerary(id, { ...updatedRecord, travel_month: updatedRecord.travel_month });
      setSaving(false);
    } catch (e) {
      toast.error("Failed to save payment");
      setSaving(false);
    }
  };

  const handleEditTransaction = (tx) => {
    setPaymentForm({
      date: tx.date || '',
      type: tx.type || 'IN',
      party: tx.party || '',
      bankAccount: tx.bankAccount || '',
      amount: tx.amount || '',
      reference: tx.reference || '',
      notes: tx.notes || ''
    });
    setEditingTxId(tx.id);
    setLedgerDialog(true);
  };

  const handleDeleteTransaction = async (txId) => {
    try {
      const updatedRecord = { ...record };
      updatedRecord.itinerary_data.ledger.transactions = updatedRecord.itinerary_data.ledger.transactions.filter(t => t.id !== txId);
      setRecord(updatedRecord);
      toast.success("Transaction deleted");
      
      setSaving(true);
      await api.updateItinerary(id, { ...updatedRecord, travel_month: updatedRecord.travel_month });
      setSaving(false);
    } catch (e) {
      toast.error("Failed to delete transaction");
    }
  };

  const handleAnalyzeReceipt = async () => {
    if (!receiptForm.file) return;
    setReceiptForm({ ...receiptForm, loading: true });
    try {
      const vendorNames = expectedVendors.map(v => v.name).join(", ");
      const custName = record?.customer_name || "Customer";
      const extendedContext = `Known Vendors: ${vendorNames}\nKnown Customer: ${custName}\nUser Note: ${receiptForm.context}`;
      
      const data = await api.analyzeReceipt(id, receiptForm.file, extendedContext);
      setReceiptDialog(false);
      setPaymentForm({
        date: data.date || new Date().toISOString().split('T')[0],
        type: data.type === 'OUT' ? 'OUT' : 'IN',
        party: data.type === 'OUT' ? (data.party || '') : (record?.customer_name || ''),
        amount: data.amount || '',
        bankAccount: data.bankAccount || '',
        reference: data.reference || '',
        notes: data.notes || ''
      });
      if (data.clarification_needed) {
        setClarificationMsg(data.clarification_message || "Please verify the AI-extracted details carefully.");
      } else {
        setClarificationMsg('');
      }
      setLedgerDialog(true);
      toast.success("Receipt scanned successfully!");
    } catch (e) {
      toast.error("Failed to analyze receipt. It may be too blurry.");
    } finally {
      setReceiptForm({ ...receiptForm, loading: false });
    }
  };

  const handleInvoiceSubmit = async () => {
    try {
      const sp = parseFloat(invoiceForm.sellingPrice) || 0;
      
      const sumCost = (arr) => arr.reduce((acc, curr) => acc + (parseFloat(curr.cost) || 0), 0);
      const totalHotelCost = sumCost(invoiceForm.hotels);
      const totalTransportCost = sumCost(invoiceForm.transports);
      const totalActivityCost = sumCost(invoiceForm.activities);
      
      const totalCost = totalHotelCost + totalTransportCost + totalActivityCost;
      const profit = sp - totalCost;
      
      if (profit < 0) {
        toast.error("Total Cost exceeds Selling Price. Profit cannot be negative.");
        return;
      }
      
      // Calculate GST on gross profit
      const netProfit = profit / 1.18;
      const gst = profit - netProfit;
      
      // Randomize the net profit into three categories roughly 30-40-30
      const convRatio = 0.3 + (Math.random() * 0.1);
      const platRatio = 0.3 + (Math.random() * 0.1);
      const miscRatio = 1.0 - (convRatio + platRatio);
      
      const convCharges = netProfit * convRatio;
      const platCharges = netProfit * platRatio;
      const miscCharges = netProfit * miscRatio;
      
      const invoiceData = {
        date: invoiceForm.date,
        packageIdx: invoiceForm.packageIdx,
        sellingPrice: sp,
        charges: {
          hotels: invoiceForm.hotels.map(h => ({ name: h.name, cost: parseFloat(h.cost) || 0 })),
          transports: invoiceForm.transports.map(t => ({ name: t.name, cost: parseFloat(t.cost) || 0 })),
          activities: invoiceForm.activities.map(a => ({ name: a.name, cost: parseFloat(a.cost) || 0 }))
        },
        profitBreakdown: {
          convenienceCharges: convCharges,
          platformCharges: platCharges,
          miscCharges: miscCharges,
          gst: gst
        },
        totalProfitGross: profit,
        totalAmount: sp
      };

      const updatedRecord = { ...record };
      if (!updatedRecord.itinerary_data) updatedRecord.itinerary_data = {};
      updatedRecord.itinerary_data.invoice = invoiceData;
      updatedRecord.status = "confirmed";
      setRecord(updatedRecord);
      
      setInvoiceDialog(false);
      toast.success("Invoice generated and itinerary confirmed!");
      
      // Auto-save
      setSaving(true);
      const payload = {
        ...updatedRecord,
        travel_month: updatedRecord.travel_month
      };
      await api.updateItinerary(id, payload);
      setSaving(false);
      
    } catch (e) {
      toast.error("Failed to generate invoice");
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_name: record.customer_name,
        customer_contact: record.customer_contact,
        customer_email: record.customer_email,
        destination: record.destination,
        travel_month: record.travel_month,
        travelers_adults: record.travelers_adults,
        travelers_children: record.travelers_children,
        travelers_infants: record.travelers_infants || 0,
        trip_type: record.trip_type,
        hotel_info: record.hotel_info,
        packages: record.packages,
        itinerary_data: record.itinerary_data,
        inclusions: record.inclusions,
        exclusions: record.exclusions,
        terms_conditions: record.terms_conditions,
        status: record.status,
      };
      
      // Save hotels to master DB
      record.itinerary_data?.days?.forEach(d => {
        const stayCity = (d.accommodation || "").trim();
        const isNoStay = !stayCity || ["na", "n/a", "nil", "none", "-", "--"].includes(stayCity.toLowerCase());
        if (!isNoStay) {
          if (d.package_hotels) {
            d.package_hotels.forEach(ph => {
              if (ph && ph.hotel_name) {
                api.saveHotel({
                  name: ph.hotel_name,
                  location: stayCity,
                  category: ph.hotel_category || "3-star",
                  images: ph.hotel_images || []
                }).catch(console.error); // Best effort
              }
            });
          } else if (d.hotel_name) {
            api.saveHotel({
              name: d.hotel_name,
              location: stayCity,
              category: d.hotel_category || "3-star",
              images: d.hotel_images || []
            }).catch(console.error); // Best effort
          }
        }
      });

      await api.updateItinerary(id, payload);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleAiUpdate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter some instructions for the AI");
      return;
    }
    setRegenLoading(true);
    try {
      const day = days[activeDay];
      const res = await api.regenerateDay(id, { day_number: day.day_number, tone: "engaging", focus: aiPrompt });
      // Apply
      setRecord((r) => {
        const copy = JSON.parse(JSON.stringify(r));
        copy.itinerary_data.days[activeDay] = res.day;
        return copy;
      });
      toast.success(`Day ${day.day_number} updated by AI`);
      setAiPrompt("");
    } catch (e) {
      toast.error("AI update failed");
    } finally {
      setRegenLoading(false);
    }
  };

  const searchImages = async () => {
    if (!imgQuery.trim()) return;
    setImgLoading(true);
    try {
      const r = await api.searchImages(imgQuery, 16);
      setImgResults(r.images || []);
    } catch {
      toast.error("Image search failed");
    } finally {
      setImgLoading(false);
    }
  };

  const swapImage = (img) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      const dayImgs = copy.itinerary_data.days[imgDialog.dayIdx].images || [];
      if (imgDialog.imgIdx === "add") {
        dayImgs.push(img);
      } else {
        dayImgs[imgDialog.imgIdx] = img;
      }
      copy.itinerary_data.days[imgDialog.dayIdx].images = dayImgs;
      return copy;
    });
    setImgDialog({ open: false, dayIdx: null, imgIdx: null });
    toast.success("Image updated. Save to persist.");
  };

  const removeImage = (dayIdx, imgIdx) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      copy.itinerary_data.days[dayIdx].images = copy.itinerary_data.days[dayIdx].images.filter((_, i) => i !== imgIdx);
      return copy;
    });
  };

  const uploadMainImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const newImg = { id: Date.now().toString(), url: ev.target.result, thumb: ev.target.result, alt: file.name };
      swapImage(newImg);
    };
    reader.readAsDataURL(file);
  };

  const uploadHotelImage = async (e, dayIdx) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ url: ev.target.result, name: file.name });
        reader.readAsDataURL(file);
      });
    });
    
    const newImages = await Promise.all(promises);
    
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      const day = copy.itinerary_data.days[dayIdx];
      if (!day.hotel_images) day.hotel_images = [];
      day.hotel_images = [...day.hotel_images, ...newImages].slice(0, 3); // Max 3
      return copy;
    });
    toast.success("Photos added (max 3 allowed) — save to persist.");
  };

  const removeHotelImage = (dayIdx, imgIdx) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      copy.itinerary_data.days[dayIdx].hotel_images =
        (copy.itinerary_data.days[dayIdx].hotel_images || []).filter((_, i) => i !== imgIdx);
      return copy;
    });
  };

  const updateDayPackageHotelField = (dayIdx, pkgIdx, field, val) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      const day = copy.itinerary_data.days[dayIdx];
      if (!day.package_hotels) day.package_hotels = [];
      if (!day.package_hotels[pkgIdx]) day.package_hotels[pkgIdx] = {};
      day.package_hotels[pkgIdx][field] = val;
      return copy;
    });
  };

  const uploadDayPackageHotelImage = async (e, dayIdx, pkgIdx) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    const promises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ url: ev.target.result, name: file.name });
        reader.readAsDataURL(file);
      });
    });
    
    const newImages = await Promise.all(promises);
    
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      const day = copy.itinerary_data.days[dayIdx];
      if (!day.package_hotels) day.package_hotels = [];
      if (!day.package_hotels[pkgIdx]) day.package_hotels[pkgIdx] = {};
      const pkgHotel = day.package_hotels[pkgIdx];
      if (!pkgHotel.hotel_images) pkgHotel.hotel_images = [];
      pkgHotel.hotel_images = [...pkgHotel.hotel_images, ...newImages].slice(0, 3); // Max 3
      return copy;
    });
    toast.success("Photos added (max 3 allowed) — save to persist.");
  };

  const removeDayPackageHotelImage = (dayIdx, pkgIdx, imgIdx) => {
    setRecord((r) => {
      const copy = JSON.parse(JSON.stringify(r));
      const pkgHotel = copy.itinerary_data.days[dayIdx].package_hotels[pkgIdx];
      pkgHotel.hotel_images = (pkgHotel.hotel_images || []).filter((_, i) => i !== imgIdx);
      return copy;
    });
  };

  const addPackage = () => {
    setRecord((r) => ({ ...r, packages: [...(r.packages || []), { name: "New Package", category: "", hotel_name: "", transport: "", price_per_adult: "", price_per_child: "", price_per_infant: "", price_total: "", notes: "" }] }));
  };

  const removePackage = (i) => {
    setRecord((r) => ({ ...r, packages: r.packages.filter((_, idx) => idx !== i) }));
  };

  const updatePackage = (i, field, val) => {
    setRecord((r) => {
      const packages = [...r.packages];
      packages[i] = { ...packages[i], [field]: val };
      return { ...r, packages };
    });
  };

  const autoCalcPackageTotal = (i) => {
    setRecord((r) => {
      const packages = [...r.packages];
      const p = packages[i];
      const ad = Number(p.price_per_adult || 0) * Number(r.travelers_adults || 0);
      const ch = Number(p.price_per_child || 0) * Number(r.travelers_children || 0);
      const inf = Number(p.price_per_infant || 0) * Number(r.travelers_infants || 0);
      packages[i] = { ...p, price_total: ad + ch + inf };
      return { ...r, packages };
    });
  };

  const day = days[activeDay];

  const expectedVendors = (() => {
    if (!record?.itinerary_data?.invoice?.charges) return [];
    const { hotels = [], transports = [], activities = [] } = record.itinerary_data.invoice.charges;
    return [...hotels, ...transports, ...activities].filter(v => v.name).map(v => ({ name: v.name, cost: Number(v.cost) || 0 }));
  })();

  const getVendorOutstanding = (vendorName) => {
    const vendor = expectedVendors.find(v => v.name === vendorName);
    if (!vendor) return 0;
    const totalExpected = vendor.cost;
    const totalPaid = (record?.itinerary_data?.ledger?.transactions || [])
      .filter(t => t.type === 'OUT' && t.party === vendorName)
      .reduce((sum, t) => sum + Number(t.amount), 0);
    return totalExpected - totalPaid;
  };
  return (
    <div className="fixed inset-0 top-14 z-30 bg-transparent pb-16 lg:relative lg:top-0 lg:z-auto lg:pb-0 p-4 lg:p-6 max-w-[1600px] w-full mx-auto flex flex-col h-[calc(100vh-56px)] lg:h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 shrink-0 px-2 lg:px-0 mt-2 lg:mt-0">
        <div className="flex items-center gap-3">
          <Link to="/admin/itineraries" className="lg:hidden"><Button variant="ghost" size="icon" className="h-8 w-8 -ml-2 text-slate-800 hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></Button></Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-display font-bold text-slate-800 truncate leading-tight">{data.trip_title || record.destination}</h1>
            <div className="text-xs md:text-sm text-slate-500 mt-0.5">{record.customer_name} · {record.days}D/{record.nights}N</div>
          </div>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
          {!showPreviewPane && !isMobile && activeEditorTab !== 'finances' && activeEditorTab !== 'preview' && (
            <Button variant="outline" size="sm" onClick={() => setShowPreviewPane(true)} className="rounded-xl border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm shrink-0">
              <Eye className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Show Preview</span><span className="sm:hidden">Preview</span>
            </Button>
          )}
          <Button variant="outline" size="sm" className="rounded-xl border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm shrink-0" onClick={() => { setShareDialog(true); setShareResult(null); }} data-testid="editor-share-button">
            <Share2 className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Share Link</span><span className="sm:hidden">Share</span>
          </Button>
          <a href={`/print/${id}`} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block"><Button variant="outline" size="sm" className="rounded-xl shrink-0 border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm" data-testid="editor-print-preview-button"><FileDown className="w-4 h-4 mr-2" /> Print</Button></a>
          <a href={`/print/${id}?autoprint=1`} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block"><Button variant="outline" size="sm" className="rounded-xl shrink-0 border-slate-300 bg-white text-slate-800 hover:bg-slate-50 shadow-sm" data-testid="editor-download-pdf-button"><FileDown className="w-4 h-4 mr-2" /> PDF</Button></a>
          
          {record.status !== "confirmed" ? (
            <Button size="sm" onClick={() => setInvoiceDialog(true)} className="rounded-xl bg-green-600 hover:bg-green-700 text-white shadow-sm shrink-0">
              <span className="hidden sm:inline">Mark as Confirmed</span><span className="sm:hidden">Confirm</span>
            </Button>
          ) : (
            <Button size="sm" onClick={() => setViewInvoiceDialog(true)} variant="outline" className="rounded-xl border-green-500 text-green-600 bg-white hover:bg-green-50 shadow-sm shrink-0">
              <span className="hidden sm:inline">View Invoice</span><span className="sm:hidden">Invoice</span>
            </Button>
          )}

          <Button size="sm" onClick={save} disabled={saving} className="rounded-xl btn-luxe  text-white shrink-0" data-testid="editor-save-button">
            {saving ? <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin text-slate-800" /> : <Save className="w-4 h-4 mr-1 md:mr-2 text-slate-800" />}
            Save
          </Button>
        </div>
      </div>

      {/* ── SHARE LINK MODAL ── */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#054A4D]">
              <Share2 className="w-5 h-5" /> Share Itinerary Link
            </DialogTitle>
          </DialogHeader>
          {!shareResult ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">Generate a temporary public link for your client to view this itinerary with a premium experience. Link will auto-expire.</p>
              <div>
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1 mb-2"><Clock className="w-4 h-4" /> Link valid for:</Label>
                <div className="flex gap-2">
                  {[1, 2, 3].map(d => (
                    <button key={d} onClick={() => setShareDays(d)}
                      className={`flex-1 py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                        shareDays === d ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-200 text-slate-500 hover:border-teal-300"
                      }`}>
                      {d} Day{d > 1 ? "s" : ""}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">Maximum 3 days. After expiry, generate a new link.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <div className="text-xs text-teal-600 font-semibold mb-2 uppercase tracking-wide">🔗 Shareable Link</div>
                <div className="flex items-center gap-2">
                  <input readOnly value={shareResult.url} className="flex-1 text-xs bg-white border border-teal-200 rounded-lg px-3 py-2 font-mono text-slate-700" />
                  <Button size="sm" variant="outline" className="rounded-lg shrink-0" onClick={() => { navigator.clipboard.writeText(shareResult.url); toast.success("Link copied!"); }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-orange-400" />
                Expires: <span className="font-semibold text-slate-700">{new Date(shareResult.expires_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
              <p className="text-xs text-slate-500">Share this link with your client. It will open a premium interactive itinerary view.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShareDialog(false)} className="rounded-xl">Close</Button>
            {!shareResult ? (
              <Button disabled={shareLoading} className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={async () => {
                setShareLoading(true);
                try {
                  const res = await api.generateShareLink(id, shareDays);
                  const url = `${window.location.origin}/share/${res.token}`;
                  setShareResult({ ...res, url });
                } catch (e) { toast.error("Failed to generate link"); }
                finally { setShareLoading(false); }
              }}>
                {shareLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Share2 className="w-4 h-4 mr-2" /> Generate Link</>}
              </Button>
            ) : (
              <Button className="rounded-xl bg-teal-600 hover:bg-teal-700" onClick={() => { navigator.clipboard.writeText(shareResult.url); toast.success("Copied!"); }}>
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0 items-stretch rounded-2xl pb-20 lg:pb-0">
        <ResizablePanel defaultSize={activeEditorTab === 'finances' ? 100 : 50} minSize={30} className={`${activeEditorTab === 'finances' ? '' : 'lg:pr-2'} flex flex-col h-full`}>
          {/* LEFT - EDITOR */}
          <Card className="rounded-2xl glass-panel-dark border-slate-200 overflow-hidden flex flex-col h-full shadow-lg lg:shadow-sm">
            <Tabs value={activeEditorTab} onValueChange={setActiveEditorTab} className="w-full flex flex-col h-full">
              {/* DESKTOP TABS */}
              <TabsList className="hidden w-full justify-start rounded-none border-b border-slate-200 bg-[#faf9f6] p-3 flex-wrap shrink-0 lg:flex text-slate-600 gap-2 h-auto font-medium shadow-[inset_0_-1px_0_rgba(0,0,0,0.05)]">
                <TabsTrigger value="trip" data-testid="editor-tab-trip" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Trip</TabsTrigger>
                <TabsTrigger value="days" data-testid="editor-tab-days" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Days</TabsTrigger>
                <TabsTrigger value="hotel" data-testid="editor-tab-hotel" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Hotel</TabsTrigger>
                <TabsTrigger value="packages" data-testid="editor-tab-packages" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Pricing</TabsTrigger>
                <TabsTrigger value="inclusions" data-testid="editor-tab-inclusions" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Inc/Exc</TabsTrigger>
                <TabsTrigger value="tc" data-testid="editor-tab-tc" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">T&C</TabsTrigger>
                <TabsTrigger value="finances" data-testid="editor-tab-finances" className="rounded-xl px-5 py-2.5 bg-white border border-slate-200 shadow-sm data-[state=active]:bg-[#ea580c] data-[state=active]:border-[#ea580c] data-[state=active]:text-white data-[state=active]:shadow-md hover:bg-slate-50 transition-all text-sm font-semibold">Finances</TabsTrigger>
              </TabsList>

              <div className="p-3 lg:p-5 flex-1 overflow-y-auto">
              <TabsContent value="preview" className="h-full mt-0">
                <div className="h-full overflow-y-auto -m-3 lg:-m-5 rounded-2xl overflow-hidden">
                   <ItineraryPreview record={record} />
                </div>
              </TabsContent>
              <TabsContent value="trip" className="space-y-4 mt-0">
                <div><Label className="text-slate-800">Trip Title</Label><Input value={data.trip_title || ""} onChange={(e) => updateField("itinerary_data.trip_title", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                <div><Label className="text-slate-800">Route Summary</Label><Input value={data.route_summary || ""} onChange={(e) => updateField("itinerary_data.route_summary", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                <div><Label className="text-slate-800">Overview</Label><Textarea rows={4} value={data.overview || ""} onChange={(e) => updateField("itinerary_data.overview", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-slate-800">Customer Name</Label><Input value={record.customer_name || ""} onChange={(e) => updateField("customer_name", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                  <div><Label className="text-slate-800">Contact</Label><Input value={record.customer_contact || ""} onChange={(e) => updateField("customer_contact", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                  <div><Label className="text-slate-800">Email</Label><Input value={record.customer_email || ""} onChange={(e) => updateField("customer_email", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                  <div><Label className="text-slate-800">Travel Date</Label><Input value={record.travel_month || ""} onChange={(e) => updateField("travel_month", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => updateField("status", record.status === "confirmed" ? "draft" : "confirmed")} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100">
                    {record.status === "confirmed" ? "Mark as Draft" : "Mark Confirmed"}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="days" className="mt-0">
                <div className="flex gap-2 flex-wrap mb-3">
                  {days.map((d, i) => (
                    <Button key={i} size="sm" variant="outline" onClick={() => setActiveDay(i)} className={`rounded-xl ${i === activeDay ? "bg-[#ea580c] text-white border-transparent shadow-md hover:bg-[#c2410c]" : "border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-[#ea580c]"}`} data-testid={`day-tab-${i}`}>
                      Day {d.day_number}
                    </Button>
                  ))}
                </div>
                {day && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-base font-display text-slate-800">Day {day.day_number} · {day.title}</Label>
                    </div>
                    
                    {/* Inline AI Chat Editor */}
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 flex flex-col gap-2 mb-4">
                      <Label className="text-xs text-orange-500 font-semibold flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" /> AI Assistant</Label>
                      <div className="flex gap-2">
                        <Input 
                          placeholder="e.g., Change accommodation to 5-star and add a romantic dinner..."
                          className="bg-white/50 rounded-lg text-sm border-slate-200 text-slate-800 placeholder-slate-400"
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          onKeyDown={(e) => { if(e.key === 'Enter') handleAiUpdate(); }}
                        />
                        <Button 
                          size="sm" 
                          onClick={handleAiUpdate} 
                          disabled={regenLoading} 
                          className="bg-blue-600 hover:bg-blue-700 rounded-lg text-white shrink-0"
                        >
                          {regenLoading ? <Loader2 className="w-4 h-4 animate-spin text-slate-800" /> : "Update Day"}
                        </Button>
                      </div>
                      <p className="text-[10px] text-orange-500/70">Tell the AI what changes you want for this day. It will instantly rewrite the details.</p>
                    </div>

                    <div><Label className="text-slate-800">Title</Label><Input value={day.title || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.title`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div><Label className="text-slate-800">Route</Label><Input value={day.route || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.route`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                      <div><Label className="text-slate-800">Distance</Label><Input value={day.distance || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.distance`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                      <div><Label className="text-slate-800">Drive Time</Label><Input value={day.drive_time || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.drive_time`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                    </div>
                    <div><Label className="text-slate-800">Description</Label><Textarea rows={6} value={day.description || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.description`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid={`day-description-${activeDay}`} /></div>
                    <div><Label className="text-slate-800">Activities (one per line)</Label><Textarea rows={3} value={(day.activities || []).join("\n")} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.activities`, e.target.value.split("\n").filter(Boolean))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label className="text-slate-800">Meals</Label><Input value={day.meals || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.meals`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                      <div><Label className="text-slate-800">Accommodation</Label><Input value={day.accommodation || ""} onChange={(e) => updateField(`itinerary_data.days.${activeDay}.accommodation`, e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" /></div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-slate-800">Images ({(day.images || []).length})</Label>
                        <Button size="sm" variant="outline" onClick={() => { setImgDialog({ open: true, dayIdx: activeDay, imgIdx: "add" }); setImgQuery(day.title || record.destination); setImgResults([]); }} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100" data-testid={`day-add-image-${activeDay}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {(day.images || []).map((img, ii) => (
                          <div key={ii} className="relative group">
                            <img src={img.url || img.thumb} className="w-full h-24 object-cover rounded-lg border" alt="" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                              <Button size="icon" variant="secondary" className="h-7 w-7 rounded-md" onClick={() => { setImgDialog({ open: true, dayIdx: activeDay, imgIdx: ii }); setImgQuery(day.title || record.destination); setImgResults([]); }} data-testid={`day-${activeDay}-swap-image-${ii}`}><RotateCw className="w-3 h-3" /></Button>
                              <Button size="icon" variant="destructive" className="h-7 w-7 rounded-md" onClick={() => removeImage(activeDay, ii)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="hotel" className="mt-0">
                <div className="mb-3">
                  <p className="text-xs text-[#ea580c] font-medium bg-orange-50 border border-orange-200 shadow-sm rounded-xl px-3 py-2">
                    🏨 Below are the AI-suggested night stays per day. Fill in the hotel name, category and upload photos for each night's stay city.
                  </p>
                </div>
                
                {(() => {
                  const nightStays = days.map((d, index) => {
                    const rawAccom = (d.accommodation || "").trim();
                    const lowerAccom = rawAccom.toLowerCase();
                    const isNoStay = !rawAccom || 
                      ["na", "n/a", "nil", "none", "-", "--"].includes(lowerAccom) || 
                      /overnight.*(travel|journey|bus|train|flight|cab|volvo)/i.test(lowerAccom) ||
                      /travel.*to/i.test(lowerAccom) ||
                      lowerAccom.includes("departure");
                    return { d, dayIdx: index, isNoStay };
                  }).filter(x => !x.isNoStay);

                  if (nightStays.length === 0) {
                    return <div className="p-4 text-center text-slate-500">No overnight stays found in this itinerary.</div>;
                  }

                  const activeNs = nightStays[activeNight] || nightStays[0];
                  const { d, dayIdx } = activeNs;

                  let stayCity = (d.accommodation || "").trim();
                  if (stayCity.toLowerCase().includes(" in ")) {
                    stayCity = stayCity.split(/ in /i).pop().trim();
                  }
                  
                  const hotelName = d.hotel_name || "";
                  const hotelCat = d.hotel_category || "";
                  const hotelNotes = d.hotel_notes || "";
                  const hotelImages = d.hotel_images || [];

                  return (
                    <div className="space-y-4">
                      <div className="flex overflow-x-auto gap-2 pb-2 mb-2 scrollbar-hide">
                        {nightStays.map((ns, i) => (
                          <Button key={i} size="sm" variant={i === activeNight ? "default" : "outline"} onClick={() => setActiveNight(i)} className={`rounded-xl transition-all ${i === activeNight ? "bg-[#ea580c] hover:bg-[#c2410c] text-white border-transparent shadow-md" : "text-slate-700 bg-white border-slate-300 shadow-sm hover:bg-slate-50 hover:text-[#ea580c]"}`}>
                            Night {ns.d.day_number}
                          </Button>
                        ))}
                      </div>

                      <Card key={dayIdx} className="rounded-xl glass-panel-dark border-slate-200 overflow-hidden">
                        {/* Day header */}
                        <div className="bg-orange-50 px-4 py-3 flex items-center justify-between border-b border-orange-100">
                          <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-full bg-[#ea580c] text-white text-sm font-bold flex items-center justify-center">{d.day_number}</span>
                            <span className="text-base font-semibold text-[#ea580c]">Night {d.day_number}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-orange-600 bg-orange-100/50 border border-orange-200 rounded-full px-3 py-1 font-medium">📍 {stayCity}</span>
                            {nightStays.findIndex(ns => ns.dayIdx === dayIdx) < nightStays.length - 1 && (
                              <div className="flex items-center gap-1 ml-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 text-[#ea580c] hover:text-[#c2410c] hover:bg-orange-100 text-xs px-3 rounded-full hidden md:flex"
                                  title="Copy hotel to all subsequent nights"
                                  onClick={() => {
                                    const currentIndex = nightStays.findIndex(ns => ns.dayIdx === dayIdx);
                                    setRecord(r => {
                                      const copy = JSON.parse(JSON.stringify(r));
                                      const src = copy.itinerary_data.days[dayIdx];
                                      for(let i = currentIndex + 1; i < nightStays.length; i++) {
                                        const dst = copy.itinerary_data.days[nightStays[i].dayIdx];
                                        dst.hotel_name = src.hotel_name;
                                        dst.hotel_category = src.hotel_category;
                                        dst.hotel_notes = src.hotel_notes;
                                        dst.hotel_images = src.hotel_images ? JSON.parse(JSON.stringify(src.hotel_images)) : [];
                                        if (src.package_hotels) dst.package_hotels = JSON.parse(JSON.stringify(src.package_hotels));
                                      }
                                      return copy;
                                    });
                                    toast.success("Copied to all remaining nights");
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" /> Copy to All Next
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 text-[#ea580c] hover:text-[#c2410c] hover:bg-orange-100 text-xs px-3 rounded-full"
                                  title="Copy hotel to next night only"
                                  onClick={() => {
                                    const currentIndex = nightStays.findIndex(ns => ns.dayIdx === dayIdx);
                                    const nextNs = nightStays[currentIndex + 1];
                                    setRecord(r => {
                                      const copy = JSON.parse(JSON.stringify(r));
                                      const src = copy.itinerary_data.days[dayIdx];
                                      const dst = copy.itinerary_data.days[nextNs.dayIdx];
                                      dst.hotel_name = src.hotel_name;
                                      dst.hotel_category = src.hotel_category;
                                      dst.hotel_notes = src.hotel_notes;
                                      dst.hotel_images = src.hotel_images ? JSON.parse(JSON.stringify(src.hotel_images)) : [];
                                      if (src.package_hotels) dst.package_hotels = JSON.parse(JSON.stringify(src.package_hotels));
                                      return copy;
                                    });
                                    toast.success(`Copied to Night ${nextNs.d.day_number}`);
                                    setActiveNight(currentIndex + 1);
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" /> Next Night
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          {(!record.packages || record.packages.length === 0) ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2 md:col-span-1">
                                  <Label className="text-xs text-slate-800">Hotel Name</Label>
                                  <Input
                                    list={`hotel-list-${dayIdx}`}
                                    value={hotelName}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      updateField(`itinerary_data.days.${dayIdx}.hotel_name`, val);
                                      const matched = (hotelSuggestions[dayIdx] || []).find(h => h.name === val);
                                      if (matched) {
                                        setRecord(r => {
                                          const copy = JSON.parse(JSON.stringify(r));
                                          const day = copy.itinerary_data.days[dayIdx];
                                          if (matched.category) day.hotel_category = matched.category;
                                          if (matched.images && matched.images.length > 0) day.hotel_images = matched.images.slice(0, 3);
                                          return copy;
                                        });
                                        toast.success("Hotel details auto-filled!");
                                      }
                                    }}
                                    placeholder={`Hotel in ${stayCity || 'city'}`}
                                    className="rounded-xl bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400 mt-1"
                                  />
                                  <datalist id={`hotel-list-${dayIdx}`}>
                                    {(hotelSuggestions[dayIdx] || []).map((h, i) => (
                                      <option key={i} value={h.name} />
                                    ))}
                                  </datalist>
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-800">Category</Label>
                                  <Select
                                    value={hotelCat}
                                    onValueChange={(v) => updateField(`itinerary_data.days.${dayIdx}.hotel_category`, v)}
                                  >
                                    <SelectTrigger className="rounded-xl bg-white/50 border-slate-200 text-slate-800 mt-1"><SelectValue placeholder="3/4/5-star…" /></SelectTrigger>
                                    <SelectContent className="bg-white border-slate-200 text-slate-800">
                                      <SelectItem value="Budget">Budget</SelectItem>
                                      <SelectItem value="3-star">3-star</SelectItem>
                                      <SelectItem value="4-star">4-star</SelectItem>
                                      <SelectItem value="5-star">5-star</SelectItem>
                                      <SelectItem value="Luxury">Luxury Resort</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs text-slate-800">Notes (room type, inclusions, etc.)</Label>
                                  <Input
                                    value={hotelNotes}
                                    onChange={(e) => updateField(`itinerary_data.days.${dayIdx}.hotel_notes`, e.target.value)}
                                    placeholder="e.g., Deluxe room with breakfast included"
                                    className="rounded-xl bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400 mt-1"
                                  />
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs text-slate-800">Hotel Photos ({hotelImages.length}/3)</Label>
                                  <label htmlFor={`hotel-upload-${dayIdx}`}>
                                    <Button variant="outline" size="sm" className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100" asChild>
                                      <span><Upload className="w-3.5 h-3.5 mr-1" /> Add Photos</span>
                                    </Button>
                                  </label>
                                  <input id={`hotel-upload-${dayIdx}`} type="file" accept="image/*" multiple className="hidden"
                                    onChange={(e) => uploadHotelImage(e, dayIdx)} />
                                </div>
                                {hotelImages.length > 0 && (
                                  <div className="grid grid-cols-3 gap-2">
                                    {hotelImages.map((img, ii) => (
                                      <div key={ii} className="relative group">
                                        <img src={img.url} className="w-full h-20 object-cover rounded-lg border" alt="" />
                                        <button
                                          onClick={() => removeHotelImage(dayIdx, ii)}
                                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                        >×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {record.packages.map((pkg, pkgIdx) => {
                                const pkgHotel = (d.package_hotels || [])[pkgIdx] || {};
                                const pHotelName = pkgHotel.hotel_name || "";
                                const pHotelCat = pkgHotel.hotel_category || "";
                                const pHotelNotes = pkgHotel.hotel_notes || "";
                                const pHotelImages = pkgHotel.hotel_images || [];
                                return (
                                  <div key={pkgIdx} className="bg-white border border-orange-100 rounded-xl p-4 shadow-sm mb-3">
                                    <div className="text-sm font-bold text-[#ea580c] uppercase tracking-wider mb-3 border-b border-slate-100 pb-2">{pkg.name} Option</div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="col-span-2 md:col-span-1">
                                        <Label className="text-xs font-semibold text-slate-700">Hotel Name</Label>
                                        <Input
                                          list={`hotel-list-${dayIdx}-${pkgIdx}`}
                                          value={pHotelName}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            updateDayPackageHotelField(dayIdx, pkgIdx, "hotel_name", val);
                                            const matched = (hotelSuggestions[dayIdx] || []).find(h => h.name === val);
                                            if (matched) {
                                              if (matched.category) updateDayPackageHotelField(dayIdx, pkgIdx, "hotel_category", matched.category);
                                              if (matched.images && matched.images.length > 0) updateDayPackageHotelField(dayIdx, pkgIdx, "hotel_images", matched.images.slice(0, 3));
                                              toast.success("Hotel details auto-filled!");
                                            }
                                          }}
                                          placeholder={`Hotel in ${stayCity || 'city'}`}
                                          className="rounded-lg bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400 mt-1 h-8 text-sm"
                                        />
                                        <datalist id={`hotel-list-${dayIdx}-${pkgIdx}`}>
                                          {(hotelSuggestions[dayIdx] || []).map((h, i) => (
                                            <option key={i} value={h.name} />
                                          ))}
                                        </datalist>
                                      </div>
                                      <div>
                                        <Label className="text-xs font-semibold text-slate-700">Category</Label>
                                        <Select
                                          value={pHotelCat}
                                          onValueChange={(v) => updateDayPackageHotelField(dayIdx, pkgIdx, "hotel_category", v)}
                                        >
                                          <SelectTrigger className="rounded-lg bg-white/50 border-slate-200 text-slate-800 mt-1 h-8 text-sm"><SelectValue placeholder="Category…" /></SelectTrigger>
                                          <SelectContent className="bg-white border-slate-200 text-slate-800">
                                            <SelectItem value="Budget">Budget</SelectItem>
                                            <SelectItem value="3-star">3-star</SelectItem>
                                            <SelectItem value="4-star">4-star</SelectItem>
                                            <SelectItem value="5-star">5-star</SelectItem>
                                            <SelectItem value="Luxury">Luxury Resort</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="col-span-2">
                                        <Label className="text-xs font-semibold text-slate-700">Notes (room type, etc.)</Label>
                                        <Input
                                          value={pHotelNotes}
                                          onChange={(e) => updateDayPackageHotelField(dayIdx, pkgIdx, "hotel_notes", e.target.value)}
                                          placeholder="e.g., Deluxe room"
                                          className="rounded-lg bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400 mt-1 h-8 text-sm"
                                        />
                                      </div>
                                    </div>
                                    <div className="mt-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs font-semibold text-slate-700">Photos ({pHotelImages.length}/3)</Label>
                                        <label htmlFor={`hotel-upload-${dayIdx}-${pkgIdx}`}>
                                          <Button variant="outline" size="sm" className="rounded-lg h-6 px-2 text-[10px] border-slate-200 text-slate-800 hover:bg-slate-100" asChild>
                                            <span><Upload className="w-3 h-3 mr-1" /> Add Photos</span>
                                          </Button>
                                        </label>
                                        <input id={`hotel-upload-${dayIdx}-${pkgIdx}`} type="file" accept="image/*" multiple className="hidden"
                                          onChange={(e) => uploadDayPackageHotelImage(e, dayIdx, pkgIdx)} />
                                      </div>
                                      {pHotelImages.length > 0 && (
                                        <div className="grid grid-cols-3 gap-2">
                                          {pHotelImages.map((img, ii) => (
                                            <div key={ii} className="relative group">
                                              <img src={img.url} className="w-full h-12 object-cover rounded-md border" alt="" />
                                              <button
                                                onClick={() => removeDayPackageHotelImage(dayIdx, pkgIdx, ii)}
                                                className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                              >×</button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                                </div>
                              )}
                            </div>
                          </Card>
                        </div>
                      );
                    })()}
                </TabsContent>

              <TabsContent value="packages" className="space-y-3 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-800">Package Options</Label>
                  <Button size="sm" variant="outline" onClick={addPackage} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                {(record.packages || []).map((p, i) => (
                  <Card key={i} className="p-3 rounded-xl bg-slate-50 border-slate-200">
                    <div className="flex gap-2 items-center mb-3">
                      <Input placeholder="Package Name" value={p.name || ""} onChange={(e) => updatePackage(i, "name", e.target.value)} className="flex-1 rounded-lg bg-white border-slate-300 text-slate-800 placeholder-slate-400 shadow-sm" />
                      <Button variant="ghost" size="icon" onClick={() => removePackage(i)} className="shrink-0 hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                    <div className="mb-3 px-1">
                      <Input placeholder="Notes / Exclusions" value={p.notes || ""} onChange={(e) => updatePackage(i, "notes", e.target.value)} className="rounded-lg w-full bg-white border-slate-300 text-slate-800 placeholder-slate-400 shadow-sm" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-start bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <div>
                         <Label className="text-xs text-slate-700 mb-1 block font-bold uppercase tracking-wider">Per Adult (₹)</Label>
                         <Input type="number" placeholder="0" value={p.price_per_adult || ""} onChange={(e) => updatePackage(i, "price_per_adult", e.target.value)} className="rounded-xl text-base bg-white border-slate-300 shadow-sm text-slate-800" />
                      </div>
                      <div>
                         <Label className="text-xs text-slate-700 mb-1 block font-bold uppercase tracking-wider">Per Child (₹)</Label>
                         <Input type="number" placeholder="0" value={p.price_per_child || ""} onChange={(e) => updatePackage(i, "price_per_child", e.target.value)} className="rounded-xl text-base bg-white border-slate-300 shadow-sm text-slate-800" />
                      </div>
                      <div>
                         <Label className="text-xs text-slate-700 mb-1 block font-bold uppercase tracking-wider">Per Infant (₹)</Label>
                         <Input type="number" placeholder="0" value={p.price_per_infant || ""} onChange={(e) => updatePackage(i, "price_per_infant", e.target.value)} className="rounded-xl text-base bg-white border-slate-300 shadow-sm text-slate-800" />
                      </div>
                      <div>
                         <div className="flex justify-between items-center mb-1">
                           <Label className="text-xs text-[#ea580c] font-bold uppercase tracking-wider">Total (₹)</Label>
                           <button className="text-xs text-[#ea580c] hover:underline hover:text-[#c2410c] font-semibold cursor-pointer" onClick={(e) => { e.preventDefault(); autoCalcPackageTotal(i); }}>Auto-Calc</button>
                         </div>
                         <Input type="number" placeholder="0" value={p.price_total || ""} onChange={(e) => updatePackage(i, "price_total", e.target.value)} className="rounded-xl text-base bg-orange-100 border-orange-200 text-slate-800 font-bold shadow-sm" />
                      </div>
                    </div>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="inclusions" className="space-y-3 mt-0">
                <div><Label className="text-slate-800">Inclusions (one per line)</Label><Textarea rows={6} value={(record.inclusions || []).join("\n")} onChange={(e) => updateField("inclusions", e.target.value.split("\n").filter(Boolean))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="inclusions-textarea" /></div>
                <div><Label className="text-slate-800">Exclusions (one per line)</Label><Textarea rows={6} value={(record.exclusions || []).join("\n")} onChange={(e) => updateField("exclusions", e.target.value.split("\n").filter(Boolean))} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400" data-testid="exclusions-textarea" /></div>
              </TabsContent>

              <TabsContent value="tc" className="space-y-3 mt-0">
                <Label className="text-slate-800">Terms & Conditions</Label>
                <Textarea rows={14} value={record.terms_conditions || ""} onChange={(e) => updateField("terms_conditions", e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 font-mono text-xs" data-testid="tc-textarea" />
              </TabsContent>

              <TabsContent value="finances" className="mt-0">
                {(!record?.itinerary_data?.invoice || record.status !== 'confirmed') ? (
                  <div className="py-20 text-center text-slate-500 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm mt-4">
                    <Lock className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Finances Locked</h3>
                    <p className="max-w-xs mx-auto text-sm">You must confirm this itinerary and generate an invoice before you can manage its ledger.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* FINANCIAL DASHBOARD */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Customer Side */}
                      <div className="bg-white border-2 border-blue-100 rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                        <h4 className="text-sm font-sans font-bold text-blue-700 mb-3 border-b border-slate-100 pb-2 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500"></div>Customer Receivables</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">Total to Collect</span>
                            <span className="font-semibold text-slate-800">₹ {Number(record.itinerary_data.invoice.totalAmount).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Amount Collected</span>
                            <span className="font-semibold">
                              ₹ {Number((record.itinerary_data.ledger?.transactions || []).filter(t => t.type === 'IN').reduce((a,b) => a + Number(b.amount), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-blue-200 pt-2 font-bold text-blue-800">
                            <span>Balance Due</span>
                            <span>
                              ₹ {Number(Number(record.itinerary_data.invoice.totalAmount) - (record.itinerary_data.ledger?.transactions || []).filter(t => t.type === 'IN').reduce((a,b) => a + Number(b.amount), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Vendor Side */}
                      <div className="bg-white border-2 border-orange-100 rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                        <h4 className="text-sm font-sans font-bold text-[#ea580c] mb-3 border-b border-slate-100 pb-2 flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#ea580c]"></div>Vendor Payables</h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600 font-medium">Total to Pay</span>
                            <span className="font-semibold text-slate-800">
                              ₹ {Number(
                                (record.itinerary_data.invoice.charges.hotels?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0) + 
                                (record.itinerary_data.invoice.charges.transports?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0) + 
                                (record.itinerary_data.invoice.charges.activities?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0)
                              ).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Amount Paid</span>
                            <span className="font-semibold">
                              ₹ {Number((record.itinerary_data.ledger?.transactions || []).filter(t => t.type === 'OUT').reduce((a,b) => a + Number(b.amount), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-orange-200 pt-2 font-bold text-orange-800">
                            <span>Balance Remaining</span>
                            <span>
                              ₹ {Number(
                                ((record.itinerary_data.invoice.charges.hotels?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0) + 
                                (record.itinerary_data.invoice.charges.transports?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0) + 
                                (record.itinerary_data.invoice.charges.activities?.reduce((a,b)=>a+(Number(b.cost)||0),0) || 0)) -
                                (record.itinerary_data.ledger?.transactions || []).filter(t => t.type === 'OUT').reduce((a,b) => a + Number(b.amount), 0)
                              ).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* VENDOR BREAKDOWN */}
                    <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                      <h4 className="text-sm font-sans font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Expected Vendor Payouts</h4>
                      <div className="grid grid-cols-1 gap-3 text-sm">
                        {record.itinerary_data.invoice.charges.hotels?.map((h, i) => (
                          <div key={`h-${i}`} className="flex justify-between items-center py-1 border-b border-slate-200 last:border-0">
                            <span className="text-slate-700 flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>{h.name}</span>
                            <span className="font-semibold text-slate-800">₹ {Number(h.cost).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                        ))}
                        {record.itinerary_data.invoice.charges.transports?.map((t, i) => (
                          <div key={`t-${i}`} className="flex justify-between items-center py-1 border-b border-slate-200 last:border-0">
                            <span className="text-slate-700 flex items-center"><span className="w-2 h-2 rounded-full bg-orange-500 mr-2"></span>{t.name}</span>
                            <span className="font-semibold text-slate-800">₹ {Number(t.cost).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                        ))}
                        {record.itinerary_data.invoice.charges.activities?.map((a, i) => (
                          <div key={`a-${i}`} className="flex justify-between items-center py-1 border-b border-slate-200 last:border-0">
                            <span className="text-slate-700 flex items-center"><span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>{a.name}</span>
                            <span className="font-semibold text-slate-800">₹ {Number(a.cost).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* TRANSACTIONS LOG */}
                    <div className="bg-white border-2 border-slate-100 rounded-2xl p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
                      <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                        <h4 className="text-sm font-sans font-bold text-slate-800">Transaction History</h4>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => {
                            setReceiptForm({ file: null, context: '', loading: false });
                            setReceiptDialog(true);
                          }} className="rounded-xl h-8 bg-purple-600 hover:bg-purple-700 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 lucide lucide-sparkles"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>
                            AI Scan Receipt
                          </Button>
                          <Button size="sm" onClick={() => {
                            setEditingTxId(null);
                            setClarificationMsg('');
                            setPaymentForm({
                              date: new Date().toISOString().split('T')[0],
                              type: 'IN',
                              party: record?.customer_name || '',
                              bankAccount: '',
                              amount: '',
                              reference: '',
                              notes: ''
                            });
                            setLedgerDialog(true);
                          }} className="rounded-xl h-8 btn-luxe hover:bg-[#1e3a8a] text-white">
                            <Plus className="w-3 h-3 mr-1" /> Log Payment
                          </Button>
                        </div>
                      </div>
                      
                      {record.itinerary_data.ledger?.transactions?.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-100 text-slate-600">
                              <tr>
                                <th className="py-2 px-3 font-medium rounded-l-lg">Date</th>
                                <th className="py-2 px-3 font-medium">Type</th>
                                <th className="py-2 px-3 font-medium">Party</th>
                                <th className="py-2 px-3 font-medium">Bank/Ref</th>
                                <th className="py-2 px-3 font-medium text-right">Amount</th>
                                <th className="py-2 px-3 font-medium text-center rounded-r-lg">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                              {record.itinerary_data.ledger.transactions.map((tx) => (
                                <tr key={tx.id} className="hover:bg-slate-50">
                                  <td className="py-3 px-3 text-slate-600">{tx.date}</td>
                                  <td className="py-3 px-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tx.type === 'IN' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                      {tx.type === 'IN' ? 'MONEY IN' : 'MONEY OUT'}
                                    </span>
                                  </td>
                                  <td className="py-3 px-3 font-medium text-slate-700">{tx.party || '-'}</td>
                                  <td className="py-3 px-3">
                                    <p className="text-slate-700">{tx.bankAccount || '-'}</p>
                                    {tx.reference && <p className="text-[10px] text-slate-500">Ref: {tx.reference}</p>}
                                  </td>
                                  <td className="py-3 px-3 text-right font-bold text-slate-700">₹ {Number(tx.amount).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                                  <td className="py-3 px-3 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-slate-200" onClick={() => handleEditTransaction(tx)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-slate-100" onClick={() => handleDeleteTransaction(tx.id)}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-sm text-slate-500">No transactions recorded yet.</div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
              </div>
            </Tabs>
          </Card>
        </ResizablePanel>

        {!isMobile && activeEditorTab !== 'finances' && activeEditorTab !== 'preview' && showPreviewPane && (
          <>
            <ResizableHandle withHandle className="w-1.5 bg-white/20 hover:bg-white/40 active:bg-white/60 transition-colors mx-1 cursor-col-resize rounded-full" />

            <ResizablePanel defaultSize={50} minSize={30} className="pl-2 flex flex-col h-full">
              {/* RIGHT - PREVIEW */}
              <Card className="rounded-2xl glass-panel-dark border-slate-200 overflow-hidden flex flex-col h-full">
                <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-medium">PREVIEW</span>
                    <span className="text-xs text-slate-500">Sample-style layout</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="w-6 h-6 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Open in new tab" onClick={() => window.open(`/print/${id}`, '_blank')}>
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="w-6 h-6 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Refresh Preview" onClick={() => setPreviewKey(k => k + 1)}>
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="w-6 h-6 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10" title="Close Preview" onClick={() => setShowPreviewPane(false)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <ItineraryPreview key={previewKey} record={record} />
                </div>
              </Card>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      {/* IMAGE SWAP DIALOG */}
      <Dialog open={imgDialog.open} onOpenChange={(o) => setImgDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-3xl bg-white border border-slate-200 shadow-xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-display font-bold text-[#054A4D]">{imgDialog.imgIdx === "add" ? "Add image" : "Swap image"}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 items-center">
            <Input value={imgQuery} onChange={(e) => setImgQuery(e.target.value)} placeholder="Search Pexels… (e.g., Manali Hadimba Temple)" className="rounded-xl bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 flex-1 h-10" onKeyDown={(e) => e.key === "Enter" && searchImages()} data-testid="image-search-input" />
            <Button onClick={searchImages} className="rounded-xl h-10 bg-[#054A4D] hover:bg-[#033336] text-white px-5 shadow-sm" data-testid="image-search-button">{imgLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <><Search className="w-4 h-4 mr-2" /> Search</>}</Button>
            <div className="h-8 w-px bg-slate-200 mx-2"></div>
            <label htmlFor="upload-main-image" className="shrink-0 cursor-pointer">
               <div className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 h-10 px-4 rounded-xl text-sm font-medium transition-colors shadow-sm">
                  <Upload className="w-4 h-4" /> Upload
               </div>
            </label>
            <input id="upload-main-image" type="file" accept="image/*" className="hidden" onChange={uploadMainImage} />
          </div>
          <div className="grid grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto">
            {imgResults.map((img) => (
              <button key={img.id} onClick={() => swapImage(img)} className="relative group" data-testid={`image-pick-${img.id}`}>
                <img src={img.thumb} className="w-full h-32 object-cover rounded-lg border-slate-200 hover:ring-2 hover:ring-white/50" alt={img.alt} />
              </button>
            ))}
            {imgResults.length === 0 && !imgLoading && (
              <div className="col-span-3 text-center py-10 text-slate-500 text-sm"><ImageIcon className="w-8 h-8 mx-auto opacity-30 mb-2" />Search to see images</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* ── INVOICE GENERATION MODAL ── */}
      <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
        <DialogContent className="max-w-lg rounded-2xl glass-panel-dark text-slate-800 border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Generate Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b border-slate-200 pb-4">
              <div>
                <Label className="text-slate-800">Invoice Date</Label>
                <Input type="date" value={invoiceForm.date} onChange={(e) => setInvoiceForm({...invoiceForm, date: e.target.value})} className="rounded-xl bg-white/50 border-slate-200 text-slate-800 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-slate-800">Selected Package</Label>
                <Select value={String(invoiceForm.packageIdx)} onValueChange={(v) => setInvoiceForm({...invoiceForm, packageIdx: Number(v)})}>
                  <SelectTrigger className="rounded-xl bg-white/50 border-slate-200 text-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-slate-800">
                    {(record?.packages || []).map((pkg, i) => (
                      <SelectItem key={i} value={String(i)}>{pkg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-b border-slate-200 pb-4">
              <Label className="text-orange-500 font-bold mb-2 block">Final Selling Price (₹)</Label>
              <Input type="number" min="0" value={invoiceForm.sellingPrice} onChange={(e) => setInvoiceForm({...invoiceForm, sellingPrice: e.target.value})} className="rounded-xl border-blue-500/30 bg-blue-500/10 text-white text-lg font-bold" />
              <p className="text-xs text-slate-500 mt-1">This is what the customer will see as the total.</p>
            </div>

            <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
              {/* HOTELS */}
              <div className="space-y-2">
                <div className="flex justify-between items-center"><Label className="font-semibold text-slate-800">Hotel Costs</Label><Button variant="ghost" size="sm" onClick={() => setInvoiceForm(f => ({...f, hotels: [...f.hotels, {name: "", cost: 0}]}))} className="text-slate-800 hover:bg-slate-100"><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {invoiceForm.hotels.map((h, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={h.name} placeholder="Hotel Name" onChange={(e) => { const arr = [...invoiceForm.hotels]; arr[i].name = e.target.value; setInvoiceForm({...invoiceForm, hotels: arr}); }} className="flex-1 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Input type="number" min="0" value={h.cost} placeholder="Cost" onChange={(e) => { const arr = [...invoiceForm.hotels]; arr[i].cost = e.target.value; setInvoiceForm({...invoiceForm, hotels: arr}); }} className="w-32 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Button variant="ghost" size="icon" onClick={() => { const arr = [...invoiceForm.hotels]; arr.splice(i, 1); setInvoiceForm({...invoiceForm, hotels: arr}); }} className="h-8 w-8 text-red-400 hover:bg-slate-100 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              {/* TRANSPORTS */}
              <div className="space-y-2">
                <div className="flex justify-between items-center"><Label className="font-semibold text-slate-800">Transport Costs</Label><Button variant="ghost" size="sm" onClick={() => setInvoiceForm(f => ({...f, transports: [...f.transports, {name: "", cost: 0}]}))} className="text-slate-800 hover:bg-slate-100"><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {invoiceForm.transports.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={t.name} placeholder="Transport Detail (e.g., Innova)" onChange={(e) => { const arr = [...invoiceForm.transports]; arr[i].name = e.target.value; setInvoiceForm({...invoiceForm, transports: arr}); }} className="flex-1 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Input type="number" min="0" value={t.cost} placeholder="Cost" onChange={(e) => { const arr = [...invoiceForm.transports]; arr[i].cost = e.target.value; setInvoiceForm({...invoiceForm, transports: arr}); }} className="w-32 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Button variant="ghost" size="icon" onClick={() => { const arr = [...invoiceForm.transports]; arr.splice(i, 1); setInvoiceForm({...invoiceForm, transports: arr}); }} className="h-8 w-8 text-red-400 hover:bg-slate-100 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>

              {/* ACTIVITIES */}
              <div className="space-y-2">
                <div className="flex justify-between items-center"><Label className="font-semibold text-slate-800">Activity Costs</Label><Button variant="ghost" size="sm" onClick={() => setInvoiceForm(f => ({...f, activities: [...f.activities, {name: "", cost: 0}]}))} className="text-slate-800 hover:bg-slate-100"><Plus className="w-3 h-3 mr-1" />Add</Button></div>
                {invoiceForm.activities.map((a, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={a.name} placeholder="Activity Detail" onChange={(e) => { const arr = [...invoiceForm.activities]; arr[i].name = e.target.value; setInvoiceForm({...invoiceForm, activities: arr}); }} className="flex-1 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Input type="number" min="0" value={a.cost} placeholder="Cost" onChange={(e) => { const arr = [...invoiceForm.activities]; arr[i].cost = e.target.value; setInvoiceForm({...invoiceForm, activities: arr}); }} className="w-32 rounded-xl text-sm bg-white/50 border-slate-200 text-slate-800" />
                    <Button variant="ghost" size="icon" onClick={() => { const arr = [...invoiceForm.activities]; arr.splice(i, 1); setInvoiceForm({...invoiceForm, activities: arr}); }} className="h-8 w-8 text-red-400 hover:bg-slate-100 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>

            {/* SUMMARY */}
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
              <div className="flex justify-between text-sm mb-1 text-slate-600">
                <span>Total Costs (Hotels + Transport + Activities)</span>
                <span className="font-medium text-slate-800">
                  ₹ { (invoiceForm.hotels.reduce((a,b)=>a+(parseFloat(b.cost)||0),0) + invoiceForm.transports.reduce((a,b)=>a+(parseFloat(b.cost)||0),0) + invoiceForm.activities.reduce((a,b)=>a+(parseFloat(b.cost)||0),0)).toFixed(2) }
                </span>
              </div>
              <div className="flex justify-between text-base font-bold text-green-400">
                <span>Auto-Calculated Gross Profit</span>
                <span>
                  ₹ { ( (parseFloat(invoiceForm.sellingPrice)||0) - (invoiceForm.hotels.reduce((a,b)=>a+(parseFloat(b.cost)||0),0) + invoiceForm.transports.reduce((a,b)=>a+(parseFloat(b.cost)||0),0) + invoiceForm.activities.reduce((a,b)=>a+(parseFloat(b.cost)||0),0)) ).toFixed(2) }
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleInvoiceSubmit} className="rounded-xl bg-green-600 hover:bg-green-700 text-white">Confirm & Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── VIEW INVOICE MODAL ── */}
      <Dialog open={viewInvoiceDialog} onOpenChange={setViewInvoiceDialog}>
        <DialogContent className="max-w-4xl rounded-xl p-0 overflow-hidden bg-white max-h-[95vh] flex flex-col">
          {record?.itinerary_data?.invoice ? (
            <div className="flex-1 overflow-y-auto bg-white invoice-print-area text-slate-800">
              
              <div className="p-8 pb-4">
                {/* The decorative border wrapper */}
                <div className="border border-yellow-600/50 p-6 relative">
                  
                  {/* HEADER */}
                  <div className="flex justify-between items-start mb-6 border-b border-yellow-600/20 pb-6">
                    <div>
                      <h1 className="text-2xl font-bold text-[#1E3A8A] mb-1">Happy Journey 4 U</h1>
                      <p className="text-xs font-semibold text-slate-600 mb-1">GSTIN <span className="font-normal">27AAACH7409R1Z8</span></p>
                      <p className="text-xs text-slate-600 mb-1">📞 +91 9999999999</p>
                      <p className="text-xs text-slate-600">📍 123, Travel Hub, Mumbai, Maharashtra, 400001</p>
                    </div>
                    <div className="text-right flex flex-col items-end">
                      <h2 className="text-xl font-bold text-[#1E3A8A] uppercase tracking-wider mb-2">Tax Invoice</h2>
                      <div className="border border-slate-300 text-[10px] text-slate-500 uppercase px-2 py-1 tracking-wider inline-block">
                        Original For Recipient
                      </div>
                    </div>
                  </div>

                  {/* INVOICE DETAILS */}
                  <div className="grid grid-cols-2 gap-8 mb-6">
                    <div className="flex space-x-12">
                      <div>
                        <p className="text-[10px] font-bold text-[#1E3A8A] uppercase">Invoice No.</p>
                        <p className="text-sm">{record.id?.split('-')[0].toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-[#1E3A8A] uppercase">Invoice Date</p>
                        <p className="text-sm">{record.itinerary_data.invoice.date}</p>
                      </div>
                    </div>
                  </div>

                  {/* BILL TO */}
                  <div className="mb-6">
                    <h3 className="text-[11px] font-bold text-[#1E3A8A] uppercase mb-1">Bill To</h3>
                    <p className="text-sm font-semibold">{record.customer_name || "Valued Customer"}</p>
                    {record.customer_contact && <p className="text-xs text-slate-600">{record.customer_contact}</p>}
                    <p className="text-xs font-semibold text-[#1E3A8A] mt-1">Place of Supply: <span className="font-normal text-slate-800">{record.destination}</span></p>
                  </div>

                  {/* ITEMS TABLE */}
                  <div className="mb-4">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead className="bg-[#F8F5EE]">
                        <tr>
                          <th className="py-2 px-3 font-semibold text-[#1E3A8A] text-center w-10 border-b border-t border-yellow-600/30">No</th>
                          <th className="py-2 px-3 font-semibold text-[#1E3A8A] border-b border-t border-yellow-600/30">SERVICES</th>
                          <th className="py-2 px-3 font-semibold text-[#1E3A8A] text-right border-b border-t border-yellow-600/30">Rate</th>
                          <th className="py-2 px-3 font-semibold text-[#1E3A8A] text-center border-b border-t border-yellow-600/30">Tax</th>
                          <th className="py-2 px-3 font-semibold text-[#1E3A8A] text-right border-b border-t border-yellow-600/30">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {/* Hotels */}
                        {(record.itinerary_data.invoice.charges.hotels?.length > 0) && (
                          <tr>
                            <td className="py-3 px-3 text-center align-top text-slate-500">1</td>
                            <td className="py-3 px-3 align-top">
                              <p className="font-medium text-slate-800">Hotel Accommodation</p>
                              <p className="text-[10px] text-slate-500 mt-1 leading-tight w-64 text-wrap">
                                Includes: {record.itinerary_data.invoice.charges.hotels.map(h => h.name).join(", ")}
                              </p>
                            </td>
                            <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.charges.hotels.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">0<br/>(0%)</td>
                            <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.charges.hotels.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          </tr>
                        )}
                        
                        {/* Transport */}
                        {(record.itinerary_data.invoice.charges.transports?.length > 0) && (
                          <tr>
                            <td className="py-3 px-3 text-center align-top text-slate-500">2</td>
                            <td className="py-3 px-3 align-top">
                              <p className="font-medium text-slate-800">Transportation</p>
                              <p className="text-[10px] text-slate-500 mt-1 leading-tight w-64 text-wrap">
                                Includes: {record.itinerary_data.invoice.charges.transports.map(t => t.name).join(", ")}
                              </p>
                            </td>
                            <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.charges.transports.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">0<br/>(0%)</td>
                            <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.charges.transports.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          </tr>
                        )}

                        {/* Activities */}
                        {(record.itinerary_data.invoice.charges.activities?.length > 0) && (
                          <tr>
                            <td className="py-3 px-3 text-center align-top text-slate-500">3</td>
                            <td className="py-3 px-3 align-top">
                              <p className="font-medium text-slate-800">Sightseeing & Activities</p>
                              <p className="text-[10px] text-slate-500 mt-1 leading-tight w-64 text-wrap">
                                Includes: {record.itinerary_data.invoice.charges.activities.map(a => a.name).join(", ")}
                              </p>
                            </td>
                            <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.charges.activities.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">0<br/>(0%)</td>
                            <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.charges.activities.reduce((a,b)=>a+(Number(b.cost)||0), 0)).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          </tr>
                        )}

                        {/* Convenience Charges */}
                        <tr>
                          <td className="py-3 px-3 text-center align-top text-slate-500">4</td>
                          <td className="py-3 px-3 align-top"><p className="font-medium text-slate-800">Convenience Charges</p></td>
                          <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.profitBreakdown.convenienceCharges).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">{Number(record.itinerary_data.invoice.profitBreakdown.convenienceCharges * 0.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}<br/>(18%)</td>
                          <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.profitBreakdown.convenienceCharges * 1.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        </tr>

                        {/* Platform Charges */}
                        <tr>
                          <td className="py-3 px-3 text-center align-top text-slate-500">5</td>
                          <td className="py-3 px-3 align-top"><p className="font-medium text-slate-800">Platform Charges</p></td>
                          <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.profitBreakdown.platformCharges).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">{Number(record.itinerary_data.invoice.profitBreakdown.platformCharges * 0.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}<br/>(18%)</td>
                          <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.profitBreakdown.platformCharges * 1.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        </tr>

                        {/* Misc Charges */}
                        <tr>
                          <td className="py-3 px-3 text-center align-top text-slate-500">6</td>
                          <td className="py-3 px-3 align-top"><p className="font-medium text-slate-800">Miscellaneous Charges</p></td>
                          <td className="py-3 px-3 text-right align-top">{Number(record.itinerary_data.invoice.profitBreakdown.miscCharges).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          <td className="py-3 px-3 text-center align-top text-[10px] text-slate-500">{Number(record.itinerary_data.invoice.profitBreakdown.miscCharges * 0.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}<br/>(18%)</td>
                          <td className="py-3 px-3 text-right align-top font-medium">{Number(record.itinerary_data.invoice.profitBreakdown.miscCharges * 1.18).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        </tr>

                        {/* SUBTOTAL ROW */}
                        <tr className="bg-[#F8F5EE]">
                          <td colSpan={3} className="py-2 px-3 font-bold text-[#1E3A8A] text-right uppercase border-b border-t border-yellow-600/30">SUBTOTAL</td>
                          <td className="py-2 px-3 font-bold text-center border-b border-t border-yellow-600/30 text-xs">₹ {Number(record.itinerary_data.invoice.profitBreakdown.gst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                          <td className="py-2 px-3 font-bold text-right border-b border-t border-yellow-600/30 text-xs">₹ {Number(record.itinerary_data.invoice.totalAmount).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* BOTTOM SECTION */}
                  <div className="grid grid-cols-2 gap-8 text-xs mt-6 border-t border-yellow-600/20 pt-4">
                    <div>
                      <p className="font-bold text-[#1E3A8A] mb-2">Terms & Conditions</p>
                      <ol className="list-decimal pl-3 space-y-1 text-slate-600 text-[10px]">
                        <li>Goods once sold will not be taken back or exchanged.</li>
                        <li>All disputes are subject to [ENTER_YOUR_CITY_NAME] jurisdiction only.</li>
                      </ol>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-slate-600">
                        <span>Taxable Amount</span>
                        <span>₹ {Number(record.itinerary_data.invoice.totalAmount - record.itinerary_data.invoice.profitBreakdown.gst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>IGST @18%</span>
                        <span>₹ {Number(record.itinerary_data.invoice.profitBreakdown.gst).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between font-bold text-sm border-t border-b border-slate-200 py-2">
                        <span className="text-[#1E3A8A]">Total Amount</span>
                        <span>₹ {Number(record.itinerary_data.invoice.totalAmount).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between text-slate-500 pt-1">
                        <span>Received Amount</span>
                        <span>₹ 0.00</span>
                      </div>
                    </div>
                  </div>

                  {/* AMOUNT IN WORDS */}
                  <div className="mt-8 text-xs">
                    <p className="font-bold text-[#1E3A8A] mb-1">Total Amount (in words)</p>
                    <p className="text-slate-600 capitalize">
                      {/* Note: A proper number to words converter can be added, currently showing dynamic string for demo or just static text. Ideally we use an npm package. */}
                      Rupees {Number(record.itinerary_data.invoice.totalAmount).toLocaleString('en-IN')} Only
                    </p>
                  </div>

                </div>
              </div>

            </div>
          ) : (
            <div className="py-16 text-center text-slate-500">No invoice generated yet.</div>
          )}
          <DialogFooter className="sm:justify-between flex gap-2 bg-slate-50 p-4 border-t border-slate-200 shrink-0 print-hide">
            <Button variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 font-medium" onClick={() => {
              const updated = { ...record };
              delete updated.itinerary_data.invoice;
              updated.status = "draft";
              setRecord(updated);
              setViewInvoiceDialog(false);
            }}>Cancel Invoice</Button>
            <div className="flex gap-2">
              <Button onClick={() => window.print()} variant="outline" className="rounded-xl border-[#0A1A3A] text-[#0A1A3A] hover:bg-blue-50 font-medium px-6 shadow-sm"><FileDown className="w-4 h-4 mr-2" /> Download PDF</Button>
              <Button onClick={() => window.print()} className="rounded-xl bg-[#0A1A3A] hover:bg-[#112a5e] text-slate-800 font-medium px-8 shadow-md">Print Invoice</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── LOG PAYMENT MODAL ── */}
      <Dialog open={ledgerDialog} onOpenChange={setLedgerDialog}>
        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden glass-panel-dark text-slate-800 border-slate-200">
          <DialogHeader className="p-6 bg-slate-50 border-b border-slate-200">
            <DialogTitle className="text-xl font-bold text-orange-500">Log Payment</DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            {clarificationMsg && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm p-3 rounded-xl flex gap-2 items-start">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-alert-triangle shrink-0 mt-0.5"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
                <span>{clarificationMsg}</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-800">Date</Label>
                <Input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})} className="rounded-xl mt-1 bg-white/50 border-slate-200 text-slate-800 [color-scheme:dark]" />
              </div>
              <div>
                <Label className="text-slate-800">Transaction Type</Label>
                <select 
                  className="w-full h-10 px-3 py-2 mt-1 rounded-xl border border-slate-200 bg-white/50 text-sm text-slate-800"
                  value={paymentForm.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setPaymentForm({
                      ...paymentForm, 
                      type: newType,
                      party: newType === 'IN' ? (record?.customer_name || '') : ''
                    });
                  }}
                >
                  <option value="IN">Money In (Customer)</option>
                  <option value="OUT">Money Out (Vendor)</option>
                </select>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-800">{paymentForm.type === 'IN' ? 'Customer Name' : 'Vendor Name'}</Label>
              {paymentForm.type === 'IN' ? (
                <div className="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl mt-1 text-sm text-slate-200 font-medium h-10 flex items-center">
                  {record?.customer_name || 'Customer Name Not Set'}
                </div>
              ) : (
                <div className="mt-1">
                  <select
                    className="w-full h-10 px-3 py-2 rounded-xl border border-slate-200 bg-white/50 text-sm text-slate-800"
                    value={paymentForm.party}
                    onChange={(e) => setPaymentForm({...paymentForm, party: e.target.value})}
                  >
                    <option value="">Select a Vendor...</option>
                    {expectedVendors.map((v, idx) => (
                      <option key={idx} value={v.name}>{v.name}</option>
                    ))}
                  </select>
                  {paymentForm.party && expectedVendors.some(v => v.name === paymentForm.party) && (
                    <p className="text-xs mt-1 text-slate-500">
                      Outstanding Balance: <span className="font-semibold text-orange-400">₹ {getVendorOutstanding(paymentForm.party).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-800">Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={paymentForm.amount} 
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} 
                  className="rounded-xl mt-1 font-semibold text-lg bg-white/50 border-slate-200 text-slate-800" 
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label className="text-slate-800">Bank / Cash</Label>
                <Input 
                  value={paymentForm.bankAccount} 
                  onChange={(e) => setPaymentForm({...paymentForm, bankAccount: e.target.value})} 
                  placeholder="e.g. HDFC Current"
                  className="rounded-xl mt-1 bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400" 
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-800">Reference ID (Optional)</Label>
              <Input 
                value={paymentForm.reference} 
                onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})} 
                placeholder="UPI / NEFT / Cheque No."
                className="rounded-xl mt-1 bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400" 
              />
            </div>
            
            <div>
              <Label className="text-slate-800">Notes</Label>
              <Textarea 
                value={paymentForm.notes} 
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} 
                className="rounded-xl mt-1 bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400" 
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200">
            <Button variant="outline" onClick={() => {
              setLedgerDialog(false);
              setEditingTxId(null);
            }} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100">Cancel</Button>
            <Button onClick={handlePaymentSubmit} disabled={!paymentForm.amount || !paymentForm.date} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
              {editingTxId ? "Update Payment" : "Save Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={receiptDialog} onOpenChange={setReceiptDialog}>
        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden glass-panel-dark text-slate-800 border-slate-200">
          <DialogHeader className="p-6 bg-slate-50 border-b border-slate-200">
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-purple-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-sparkles"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>
              AI Receipt Scanner
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <div>
              <Label className="text-slate-800">Upload Screenshot/Receipt</Label>
              <Input 
                type="file" 
                accept="image/*" 
                onChange={(e) => setReceiptForm({...receiptForm, file: e.target.files[0]})} 
                className="mt-1 bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400" 
              />
            </div>
            <div>
              <Label className="text-slate-800">Additional Context (Optional)</Label>
              <Input 
                placeholder="e.g. Paid to hotel via UPI" 
                value={receiptForm.context} 
                onChange={(e) => setReceiptForm({...receiptForm, context: e.target.value})} 
                className="rounded-xl mt-1 bg-white/50 border-slate-200 text-slate-800 placeholder-slate-400" 
              />
              <p className="text-xs text-slate-500 mt-1">Help the AI understand who or what this is for.</p>
            </div>
          </div>
          <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200">
            <Button variant="outline" onClick={() => setReceiptDialog(false)} className="rounded-xl border-slate-200 text-slate-800 hover:bg-slate-100">Cancel</Button>
            <Button onClick={handleAnalyzeReceipt} disabled={!receiptForm.file || receiptForm.loading} className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white flex gap-2">
              {receiptForm.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {receiptForm.loading ? "Analyzing..." : "Analyze Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MOBILE BOTTOM NAVIGATION */}
      <nav className="flex lg:hidden absolute bottom-0 left-0 right-0 glass-panel-dark border-t border-slate-200 z-[100] shadow-[0_-10px_40px_rgba(0,0,0,0.3)] pb-safe">
        <div className="flex justify-around items-center h-16 px-1 w-full max-w-md mx-auto">
          {[
            { id: "trip", icon: Map, label: "Trip" },
            { id: "days", icon: CalendarDays, label: "Days" },
            { id: "packages", icon: CircleDollarSign, label: "Pricing" },
            { id: "finances", icon: Wallet, label: "Ledger" },
            { id: "preview", icon: Eye, label: "Preview" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveEditorTab(tab.id)}
              className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-all ${
                activeEditorTab === tab.id ? "text-orange-500" : "text-slate-500 hover:text-slate-200"
              }`}
            >
              <div className={`p-1.5 rounded-full ${activeEditorTab === tab.id ? "bg-blue-500/20 text-orange-500" : ""}`}>
                <tab.icon className={`w-5 h-5 ${activeEditorTab === tab.id ? "stroke-[2.5]" : "stroke-2"}`} />
              </div>
              <span className={`text-[10px] font-medium ${activeEditorTab === tab.id ? "font-bold text-orange-500" : ""}`}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>

    </div>
  );
}

